import { type DeviceProvisionsState } from './admin-device-provisions-types'
import {
  CreateDeviceSucceededAction,
  DeleteDeviceSucceededAction,
  EditedDeviceForProvisionChangedAction,
  FetchDeviceProfilesAction,
  FetchDeviceProfilesFailedAction,
  FetchDeviceProfilesSucceededAction,
  FetchDevicesForProvisionSucceededAction,
  FetchTenantsAction,
  FetchTenantsFailedAction,
  FetchTenantsSucceededAction,
  ImportDevicesForProvisionFileChangedAction,
  ImportDevicesForProvisionProfileIdChangedAction,
  NewDeviceForProvisionValuesChangedAction,
  NewGenerateCsvFromSerialValueChangedAction,
  SearchDevicesForProvisionAction,
  SetCreateDeviceDialogStateAction,
  SetDevicesActivePageAction,
  SetDevicesPageSizeAction,
  SetEditDeviceDialogStateAction,
  SetGenerateCsvFromGivenSerialDialogStateAction,
  SetImportDevicesDialogStateAction,
  SelectedTenantChangedAction,
  UpdateDeviceSucceededAction,
  UploadDevicesCsvSucceededAction
} from './admin-device-provisions-actions'
import { createReducer } from '@reduxjs/toolkit'

const initialDeviceForProvisionFields = {
  serialNumber: '',
  devEUI: '',
  appEUI: '',
  appKey: '',
  deviceProfileId: ''
}

const initialUpdateDeviceForProvisionFields = { ...initialDeviceForProvisionFields, id: '' }

export const initialDeviceProvisionsState: DeviceProvisionsState = {
  deviceProfiles: [],
  devicesForProvision: [],
  tenants: [],
  selectedTenant: '',
  tenantLoadStatus: 'idle',
  newDeviceForProvision: { ...initialDeviceForProvisionFields },
  editedDeviceForProvision: { ...initialUpdateDeviceForProvisionFields },
  csvExportStart: '',
  editDeviceForProvisionDialogOpen: false,
  createDeviceForProvisionDialogOpen: false,
  generateCSVDialogOpen: false,
  importDevicesForProvisionDialogOpen: false,
  importDevicesForProvisionFile: null,
  importDevicesForProvisionProfileId: '',
  loadingDeviceProfiles: false,
  deviceProfilesLoaded: false,
  devicesActivePage: 1,
  devicesPageSize: 10,
  searchQuery: ''
}

/**
 * Device for provisions (admin actions) reducer.
 */
export const adminDeviceProvisionsReducer = createReducer(initialDeviceProvisionsState, builder => {
  // Thunks related Actions
  builder.addCase(CreateDeviceSucceededAction, (state, action) => {
    state.devicesForProvision.push(action.payload)
    state.newDeviceForProvision = { ...initialDeviceForProvisionFields }
    state.createDeviceForProvisionDialogOpen = false
  }).addCase(DeleteDeviceSucceededAction, (state, action) => {
    state.devicesForProvision = state.devicesForProvision.filter(item => item.id !== action.payload.id)
    state.editedDeviceForProvision = { ...initialUpdateDeviceForProvisionFields }
    state.editDeviceForProvisionDialogOpen = false
  }).addCase(FetchDeviceProfilesAction, (state, _) => {
    state.loadingDeviceProfiles = true
  }).addCase(FetchDeviceProfilesSucceededAction, (state, action) => {
    state.deviceProfiles = action.payload
    state.loadingDeviceProfiles = false
    state.deviceProfilesLoaded = true
  }).addCase(FetchTenantsAction, (state, _) => {
    state.tenantLoadStatus = 'loading'
  }).addCase(FetchTenantsSucceededAction, (state, action) => {
    state.tenants = action.payload
    state.tenantLoadStatus = 'succeeded'
  }).addCase(FetchTenantsFailedAction, (state, _) => {
    state.tenantLoadStatus = 'failed'
  }).addCase(FetchDeviceProfilesFailedAction, (state, _) => {
    state.loadingDeviceProfiles = false
  }).addCase(FetchDevicesForProvisionSucceededAction, (state, action) => {
    state.devicesForProvision = action.payload
  }).addCase(UpdateDeviceSucceededAction, (state, action) => {
    const index = state.devicesForProvision.findIndex((it) => it.id === action.payload.id)
    if (index !== -1) {
      state.devicesForProvision[index] = { ...state.devicesForProvision[index], ...action.payload }
    }
    state.editedDeviceForProvision = { ...initialUpdateDeviceForProvisionFields }
    state.editDeviceForProvisionDialogOpen = false
  }).addCase(UploadDevicesCsvSucceededAction, (state, action) => {
    state.devicesForProvision = [...state.devicesForProvision, ...action.payload]
    state.importDevicesForProvisionFile = null
    state.importDevicesForProvisionProfileId = ''
    state.importDevicesForProvisionDialogOpen = false
  })
  // Regular Actions
    .addCase(NewDeviceForProvisionValuesChangedAction, (state, action) => {
      state.newDeviceForProvision = action.payload
    }).addCase(NewGenerateCsvFromSerialValueChangedAction, (state, action) => {
      state.csvExportStart = action.payload.serialNumber
    }).addCase(EditedDeviceForProvisionChangedAction, (state, action) => {
      state.editedDeviceForProvision = action.payload
    }).addCase(ImportDevicesForProvisionFileChangedAction, (state, action) => {
      state.importDevicesForProvisionFile = action.payload.file
    }).addCase(ImportDevicesForProvisionProfileIdChangedAction, (state, action) => {
      state.importDevicesForProvisionProfileId = action.payload.profileId
    }).addCase(SearchDevicesForProvisionAction, (state, action) => {
      state.searchQuery = action.payload.searchQuery
    }).addCase(SetCreateDeviceDialogStateAction, (state, action) => {
      state.createDeviceForProvisionDialogOpen = action.payload.isOpen
      state.newDeviceForProvision = action.payload.isOpen ? state.newDeviceForProvision : { ...initialDeviceForProvisionFields }
    }).addCase(SetEditDeviceDialogStateAction, (state, action) => {
      state.editDeviceForProvisionDialogOpen = action.payload.isOpen
      state.editedDeviceForProvision = action.payload.isOpen
        ? (action.payload.editedDevice ?? initialUpdateDeviceForProvisionFields)
        : initialUpdateDeviceForProvisionFields
    }).addCase(SetGenerateCsvFromGivenSerialDialogStateAction, (state, action) => {
      state.generateCSVDialogOpen = action.payload.isOpen
    }).addCase(SetImportDevicesDialogStateAction, (state, action) => {
      state.importDevicesForProvisionDialogOpen = action.payload.isOpen
      state.importDevicesForProvisionFile = action.payload.isOpen ? state.importDevicesForProvisionFile : null
      state.importDevicesForProvisionProfileId = action.payload.isOpen ? state.importDevicesForProvisionProfileId : ''
    }).addCase(SetDevicesActivePageAction, (state, action) => {
      state.devicesActivePage = action.payload.pageNumber
    }).addCase(SetDevicesPageSizeAction, (state, action) => {
      state.devicesActivePage = 1
      state.devicesPageSize = action.payload.pageSize
    }).addCase(SelectedTenantChangedAction, (state, action) => {
      state.selectedTenant = action.payload
    })
})
